import { IMenuItem } from "@asup/context-menu";
import { useMemo } from "react";
import { AioIconButton } from "../additional/aioIconButton";
import { getBaseId } from "./functions/getBaseId";
import {
  ADD_ROW,
  AitLocationBodyRow,
  AitLocationBodyRowGroup,
  AitLocationHeader,
  AitLocationHeaderRow,
  AitTablePosition,
  DUPLICATE_ROW,
} from "./functions/interface";
import { useTableContext } from "./TableContextProvider";

interface AitAddRowButtonProps {
  location: AitLocationHeader | AitLocationHeaderRow | AitLocationBodyRowGroup | AitLocationBodyRow;
}

/**
 * Render the add-row control for a row or row group.
 *
 * @template T - Table payload type
 * @param props - Component props
 * @param props.location - Location where the new row should be added
 */
export const AitAddRowButton = <T,>({ location }: AitAddRowButtonProps) => {
  const { state, dispatch } = useTableContext<T>();
  const id = getBaseId(location, state?.id);
  const thisRowGroup =
    state &&
    (location.position === AitTablePosition.bodyRowGroup ||
      location.position === AitTablePosition.bodyRow)
      ? state.originalTableData.bodyData.find((rg) => rg.aitid === location.rowGroupId)
      : state && state.originalTableData.headerData
        ? state.originalTableData.headerData
        : null;
  const newLength = thisRowGroup ? thisRowGroup.rows.length + 1 : 0;

  const menuItems = useMemo(() => {
    const ret: IMenuItem[] = [
      {
        label: "Add row",
        action: () => {
          dispatch({
            operation: ADD_ROW,
            location,
            newLength,
          });
        },
      },
    ];
    if (
      location.position === AitTablePosition.headerRow ||
      location.position === AitTablePosition.bodyRow
    ) {
      ret.push({
        label: "Duplicate row",
        action: () =>
          dispatch({
            operation: DUPLICATE_ROW,
            location,
            newLength,
          }),
      });
    }
    return ret;
  }, [dispatch, location, newLength]);

  return !state || !state.editable || ("isRepeat" in location && location.isRepeat) ? null : (
    <AioIconButton
      id={`${id}-add-row`}
      tipText="Add row"
      iconName={"aioxPlus"}
      menuItems={menuItems}
    />
  );
};

AitAddRowButton.displayName = "AitAddRowButton";
